import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { Doctypedata } from "../../components/grid/GridData.tsx";
import { externalgetApi } from "../../services/externalApis.tsx";
import {
  SETTING_API_03,
  SETTING_API_09,
} from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_VALUE_ZERO } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";

// Types and Interfaces
interface DocumentCategory {
  doccatcode: string | number;
  doccatname: string;
  [key: string]: unknown;
}

interface DocumentType {
  doctypecode: string | number;
  doctype: string;
  doccatcode?: string | number;
  descrip?: string;
  [key: string]: unknown;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface ErrorState {
  doccatcode?: string;
  [key: string]: string | undefined;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface RowWithId extends DocumentType {
  id: string | number;
}

function ViewDocumentType(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE } = SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [datadoccat, setDatadoccat] = useState<DocumentCategory[] | null>(null);
  const [data, setData] = useState<DocumentType[] | null>(null);
  const [error, setError] = useState<ErrorState>({});
  const [selectedDoccatcode, setSelectedDoccatcode] = useState<string | number>("");
  const [filteredRows, setFilteredRows] = useState<DocumentType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    setPageData({
      title: "Document Types",
    } as PageData);
  }, [setPageData]);

  useEffect(() => {
    if (Array.isArray(datadoccat) && datadoccat.length > 0) {
      setSelectedDoccatcode(datadoccat[0].doccatcode);
    }
  }, [datadoccat]);

  const handleItemClick = (id: string | number, actionText: string): void => {
    if (actionText === "Edit") {
      const selectedItem = data?.find((item) => item.doctypecode === id);
      const selectedCategory = datadoccat?.find(
        (item) => item.doccatcode === selectedItem?.doccatcode
      );
      if (selectedItem) {
        navigate("/CreateDocumentTypes", {
          state: {
            selectedItem,
            actionText,
            selectedDoccatcode: selectedCategory?.doccatcode || "",
            selectedDoccatname: selectedCategory?.doccatname || "",
          },
        });
      }
    }
  };

  const menuItems: MenuItem[] = [
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (doctypecode: string | number) => handleItemClick(doctypecode, "Edit"),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!selectedDoccatcode) return;

      try {
        const result = await externalgetApi(SETTING_API_03, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          DOCCATCODE: selectedDoccatcode,
          STS: CON_STATUS_ACTIVE,
        });
        console.log("API result for selectedDoccatcode:", result);
        setData(result as DocumentType[]);
        setFilteredRows(result as DocumentType[]);
      } catch (err) {
        console.error("Error fetching data for selectedDoccatcode:", err);
      }
    };

    fetchData();
  }, [selectedDoccatcode, COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_09, "get", {
          STS: CON_STATUS_ACTIVE,
        });

        setDatadoccat(result as DocumentCategory[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle search input change
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);

    // Filter rows based on search query
    const filtered = data?.filter((row) =>
      row.doctype.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredRows(filtered || []);
  };

  const addToView = (): void => {
    const selectedCategory = datadoccat?.find(
      (item) => item.doccatcode === selectedDoccatcode
    );
    navigate("/CreateDocumentTypes", {
      state: {
        actionText: "Add",
        selectedDoccatcode: selectedCategory?.doccatcode || "",
        selectedDoccatname: selectedCategory?.doccatname || "",
      },
    });
  };

  useEffect(() => {
    if (Array.isArray(datadoccat) && datadoccat.length > 0) {
      setSelectedDoccatcode(datadoccat[CON_VALUE_ZERO as number].doccatcode);
    }
  }, [datadoccat]);

  // Helper functions for Autocomplete - FIXED to return undefined instead of null
  const findSelectedDocumentCategory = (): DocumentCategory | undefined => {
    if (!Array.isArray(datadoccat) || !selectedDoccatcode) return undefined;
    return datadoccat.find((item) => item.doccatcode === selectedDoccatcode);
  };

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = (filteredRows || []).map((row) => ({
    ...row,
    id: row.doctypecode || Math.random(),
  }));

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Document Type
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {Doctypedata.name}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                id="act_doccatname"
                options={Array.isArray(datadoccat) ? datadoccat : []}
                getOptionLabel={(option: DocumentCategory) => option.doccatname || ""}
                disableClearable
                fullWidth
                value={findSelectedDocumentCategory()}
                onChange={(event: React.SyntheticEvent, newValue: DocumentCategory | null) => {
                  const doccatcode = newValue ? newValue.doccatcode : "";
                  console.log("Selected doccatcode:", doccatcode);
                  setSelectedDoccatcode(doccatcode);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_doccatname"
                      {...restParams}
                      label={
                        <span>
                          Document Category Name
                          <span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.doccatcode}
                      helperText={error?.doccatcode}
                      size="small"
                      name="doccatcode"
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />

              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.doctype) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder="Search by..."
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_appbarsearch"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={Doctypedata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={Doctypedata.name}
            gridid="doctypecode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>
    </>
  );
}

export default ViewDocumentType;